import { Injectable } from "@angular/core";
// multi-asset
import * as CubiqTS from "shared/cubiq-model-multi-asset";
// global-services

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";
import { CapitalizePipe } from "app/bfx-shared/pipes/bfx-capitalize.pipe";
import { Subscription } from "rxjs";
// cubiq - services
import {
  InterventionDataPromiseLookupService,
  OrderDataPromiseLookupService,
  ScratchPadDataPromiseLookupService,
  BenchmarkDataPrimaryLookupService,
  BmarkOrderDataOperation,
  TraderGroupPromiseLookupService,
} from "shared/cubiq-services";
// order entry - services
import { OESyncService } from "../oe-sync.service";
import { OEModelGetterService } from "../model/oe-model-getter.service";
import { OEModelSetterService } from "../model/oe-model-setter.service";
import { OECoordinatorService } from "../oe-coordinator.service";
import { OEUtilityService } from "../basic/oe-utility.service";
import { OEPermissionsService } from "../basic/oe-permissions.service";
import { OELookupService } from "../basic/oe-lookup.service";
import { OELoggerService } from "../basic/oe-logger.service";
import { OESettingsService } from "../basic/oe-settings.service";
// order-entry models
import { OEUpdateEventModel } from "../../data/models/oe-update-event.model";
// order-entry enums
import { OENDisplayState } from "../../data/enums/oe-display-state.enum";
import { OENCommand } from "../../data/enums/oe-command.enum";
import { OENNotificationType } from "../../data/enums/oe-notification-type.enum";
import { OEDialogService } from "../basic/oe-dialogs.service";
import { OENotificationsService } from "../basic/oe-notifications.service";
import { IOrderEntryEvent } from "../../../../shared/cubiq-services/model/core/IOrderEntryEvent";
import { filter } from "rxjs/operators";
import { FxLifeCycleEventType } from "shared/cubiq-model-multi-asset";
import { BmarkOrderDataEventPayload } from "shared/cubiq-services/model/benchmarks/BmarkOrderDataEvent.model";
import { OENOperationMode } from "app/order-entry/data/enums/oe-operation-mode.enum";
import { OEAPIChannelService } from "app/order-entry/services/oe-api-channel.service";
import { IOrderMigrationEvent } from "shared/cubiq-services/model/core/IOrderMigrationEvent";
import { OEModelOperationsService } from "app/order-entry/services/model/oe-model-operations.service";

const BMARK_HCF_ERROR_ID = "order.activation.from.modification.during.BenchmarkHardCutoff";
const BMARK_HCF_ERROR_TEXT = "Order rejected, benchmark fixing has entered hard-cutoff";
@Injectable()
export class OEWorkerListenerService {
  /**********************************************************************/
  /* DATA */
  protected orderEntryClearSubscription: Subscription;
  protected modelOrderEntrySubscription: Subscription;
  protected bmarkEntrySubscription: Subscription;
  protected bmarkLockSubscription: Subscription;
  protected modelOrderErrorSubscription: Subscription;
  protected modelInterventionOrderEntrySubscription: Subscription;
  protected modelScratchpadOrderEntrySubscription: Subscription;
  protected modelOrderMigrationSubscription: Subscription;
  //
  private control: {
    lifeCycle: {
      eventQueue: Array<IOrderEntryEvent>;
      processingRefId: string;
      processingMessageId: string;
      processorBusy: boolean;
      processingTimeout: any;
      processingTimeoutLimit: number;
    };
  } = {
    lifeCycle: {
      eventQueue: [],
      processingRefId: null,
      processingMessageId: null,
      processorBusy: false,
      processingTimeout: null,
      processingTimeoutLimit: 3 * 1000,
    },
  };

  //
  private lifeCycleSubscribedRefIds: Set<string> = new Set<string>();

  /**********************************************************************/
  /* CONSTRUCTOR */
  constructor(
    protected oeLogger: OELoggerService,
    private translate: TranslateService,
    //
    private sync: OESyncService,
    private getter: OEModelGetterService,
    private setter: OEModelSetterService,
    private coordinator: OECoordinatorService,
    private utility: OEUtilityService,
    private permissions: OEPermissionsService,
    private lookup: OELookupService,
    private settings: OESettingsService,
    private dialogs: OEDialogService,
    private notifications: OENotificationsService,
    ///
    private orderLookup: OrderDataPromiseLookupService,
    private bmarkLookup: BenchmarkDataPrimaryLookupService,
    private interventionLookup: InterventionDataPromiseLookupService,
    private scratchpadLookup: ScratchPadDataPromiseLookupService,
    private tgLookup: TraderGroupPromiseLookupService,
    private modelOperations: OEModelOperationsService,
    //
    private capitalizePipe: CapitalizePipe,
    private channelsService: OEAPIChannelService,
  ) {
    this.oeLogger = oeLogger.for(__dirname);
    this.initialize();
  }

  /**********************************************************************/
  /* INITIALIZATION */

  private initialize(): void {
    this.subscribeToWorker();
  }

  private subscribeToWorker(): void {
    this.orderLookup.isPrimed().then(() => {
      // OrderData Lookup
      this.orderEntryClearSubscription = this.orderLookup
        .getOrderEntryClearEventSubject()
        .subscribe((event) => this.onOrderEntryClearEvent(event));
      this.modelOrderEntrySubscription = this.orderLookup
        .getModelOrderEntrySubject()
        .subscribe((event) => this.onOrderLifeCycleEvent(event));
      this.modelOrderMigrationSubscription = this.orderLookup
        .getModelOrderMigrationSubject()
        .subscribe((event) => this.onOrderMigrationEvent(event));
      this.modelOrderErrorSubscription = this.orderLookup
        .getModelErrorSubject()
        .subscribe(this.onOrderErrorEvent.bind(this));
      // Intervention Lookup
      /*
       * Temporary fix to avoid duplicate intervention events (now that interventionDataPrimary service also processes bmarks),
       * benchmark intervention is filtered here. Bmark intervention is received through bmarkLookup subscription (as it was previously).
       */
      this.modelInterventionOrderEntrySubscription = this.interventionLookup
        .getModelOrderEntrySubject()
        // InterventionDataServiuce puts an additional isBenchmark flag on the event
        .pipe(filter(($event: IOrderEntryEvent & { isBenchmark: boolean }) => !$event.isBenchmark))
        .subscribe(this.onInterventionEvent.bind(this));
      // SracthPad Lookup
      this.modelScratchpadOrderEntrySubscription = this.scratchpadLookup
        .getModelOrderEntrySubject()
        .subscribe(this.onScratchpadSaveEvent.bind(this));
    });

    this.bmarkLookup.isPrimed().then(
      (primed) => {
        this.bmarkEntrySubscription = this.bmarkLookup
          .getBmarkOrderDataUpdateSubject()
          .pipe(filter(($event) => this.relevantOEEventsFilter($event)))
          .subscribe(this.onBmarkOrderDataUpdateEvent.bind(this));
      },
      (error) => {
        // NOP
      },
    );
  }

  /**********************************************************************/
  /* SUBSCRIPTION FILTERS */

  private relevantOEEventsFilter($event: any): boolean {
    let result: boolean = false;
    if ($event != null) {
      if (Array.isArray($event)) {
        logger.out.warn(`BMARK: OE-FILTER: Ignoring batch event`, $event);
      } else {
        if ($event.payload != null && this.lifeCycleSubscribedRefIds.has($event.payload.ref)) {
          result = true;
        }
        /* logger.out.info(`BMARK: OE-FILTER: $event = source: ${BmarkOrderDataUpdateSource[$event.payload.source]} | `
				+`ref: ${$event.payload != null ? $event.payload.ref : 'N/A'} (${this.bmarkSubscribedRefIds.has($event.payload.ref) ? 'MATCH' : 'IGNORE'}) | `
				+`opr:  ${Array.isArray($event.operations) ? $event.operations.map(item=>BmarkOrderDataOperation[item]).toString() : 'N/A'} | ` 
				+`=> Result ${result ? 'PROCESS' : 'IGNORE' }  `, $event); */
      }
    }
    return result;
  }

  private onBmarkOrderDataUpdateEvent($event: any): void {
    if ($event != null) {
      if (Array.isArray($event)) {
        logger.out.warn(`BMARK: OE-WORKER-LISTENER: Received BATCH update event => `, $event);
        //
      } else {
        let actionsExecuted: Array<string> = new Array<string>();
        for (let operation of $event.operations) {
          try {
            switch (operation) {
              case BmarkOrderDataOperation.OE_DI_NEW:
              case BmarkOrderDataOperation.OE_DI_APPROVE:
              case BmarkOrderDataOperation.OE_DI_REJECT:
                this.onInterventionEvent($event.payload);
                actionsExecuted.push(
                  `${BmarkOrderDataOperation[operation]} -> onInterventionEvent()  `,
                );
                break;
              case BmarkOrderDataOperation.OE_ERROR:
                this.onOrderErrorEvent($event.payload);
                actionsExecuted.push(
                  `${BmarkOrderDataOperation[operation]} -> onOrderErrorEvent()  `,
                );
                break;
              case BmarkOrderDataOperation.OE_CLEAR:
                this.onOrderEntryClearEvent($event.payload);
                actionsExecuted.push(
                  `${BmarkOrderDataOperation[operation]} -> onOrderEntryClearEvent()  `,
                );
                break;
              case BmarkOrderDataOperation.UPDATE:
              case BmarkOrderDataOperation.OE_DI_NEEDED:
                this.onOrderLifeCycleEvent($event.payload);
                actionsExecuted.push(
                  `${BmarkOrderDataOperation[operation]} ->onOrderEntryEvent() `,
                );
                break;
              default:
                break;
            }
          } catch (ex) {
            logger.out.warn(`Error Processing `, $event, ex);
          }
        }
        logger.out.info(
          `BMARK: OE-WORKER-LISTENER: ref: ${
            $event.payload != null ? $event.payload.ref : "N/A"
          } | Operations executed: ${actionsExecuted.toString()} | Event:`,
          $event,
        );
      }
    } else {
    }
  }

  /**********************************************************************/
  /* ORDER INTERVENTION EVENT HANDLER */

  protected onInterventionEvent(event: any): void {
    //	setTimeout(()=>{ // delay added so the UPDATE event (if any) is processed first
    this.doInterventionEvent(event);
    //	}, 100);
  }

  protected doInterventionEvent(event: any): void {
    if (event != null) {
      let invEvent = event;
      if (event instanceof Array) {
        // In theory this should not happen, event shouldnt be in an array, but just in case.
        (event as Array<any>)
          .filter((item: any) => {
            return this.getter.has(item.clOrdID) ? true : false;
          })
          .forEach((item: any) => {
            invEvent = item;
          });
      }

      // overwrite refId if it has a forward refId
      let refId: string = invEvent.ref;
      if (this.sync.isTheRefIdForwarded(refId)) {
        refId = this.sync.getForwardTargetRefId(refId);
      }

      if (refId != null && typeof refId === "string" && this.getter.has(refId)) {
        //  log
        let logInvEvtText = `Inv.EVT: (${
          invEvent.invEvtType != null ? invEvent.invEvtType : "N/A"
        }) = [ ${
          invEvent.invEvtType != null ? CubiqTS.InterventionEventType[invEvent.invEvtType] : "N/A"
        } ]`;
        let logInvOprText = `Inv.OPR: (${
          invEvent.invOprType != null ? invEvent.invOprType : "N/A"
        }) = [ ${
          invEvent.invOprType != null
            ? CubiqTS.InterventionOperationType[invEvent.invOprType]
            : "N/A"
        } ]`;
        logger.out.info(
          `UI-MAIN: OrderEntry@${Date.now()} [INTERVENTION]: INTERVENTION Event (msgId: ${
            event.messageId
          }) RECEIVED => [${event.ref}] | ${logInvEvtText} | ${logInvOprText}`,
        );
        // close dialogs if any are open
        this.closeDialogIfActive(refId);

        if (this.getter.isSubscriptionToInterventionEventsActive(refId)) {
          // define refIds
          let activeRefId: string = this.getter.getActivelyDisplayedRefId(refId);
          activeRefId = activeRefId != null ? activeRefId : refId;
          let rootRefId: string = this.getter.getRootIdForNodeWithRefId(refId);
          /// Check Operation or Event Type
          if (
            (invEvent.invOprType != null && typeof invEvent.invOprType === "number") ||
            (invEvent.invEvtType != null && typeof invEvent.invEvtType === "number")
          ) {
            ///
            if (CubiqTS.OrderState.REJECTED == this.getter.getSrvOrderState(refId)) {
              // NOP
            } else if (
              invEvent.invOprType === CubiqTS.InterventionOperationType.NEW &&
              (invEvent.invEvtType === CubiqTS.InterventionEventType.LIMIT_ORDER ||
                invEvent.invEvtType === CubiqTS.InterventionEventType.BMARK_ORDER)
            ) {
              if (
                this.getter.isOperationModeAmendMarkup(refId) &&
                invEvent.reasonId === CubiqTS.DIReason.FORCE_TO_DI
              ) {
                if (invEvent.unlockUserId != null) {
                  this.lookup.userById(new CubiqTS.Long(invEvent.unlockUserId)).then(
                    (user: CubiqTS.UserImpl) => {
                      let data = {
                        message: "Unlocked by " + user.userName,
                        hideDuplicate: true,
                        hideEdit: true,
                      };
                      this.outputExtendedLogging(
                        `DEV-UNLOCK: OE-Lister.onIntervention(L-252): [${activeRefId}] -> UNLOCK | event: `,
                        event,
                      );
                      this.coordinator.cmdChangeDisplayState(
                        activeRefId,
                        OENCommand.STATE_ORDER_UNLOCKED,
                        data,
                      );
                    },
                    (error) => {
                      logger.out.info("error");
                    },
                  );
                } else {
                  let data = {
                    message: "Order unlocked",
                    hideDuplicate: true,
                    hideEdit: true,
                  };
                  this.outputExtendedLogging(
                    `DEV-UNLOCK: OE-Lister.onIntervention(L-264): [${activeRefId}] -> UNLOCK | event: `,
                    event,
                  );
                  this.coordinator.cmdChangeDisplayState(
                    activeRefId,
                    OENCommand.STATE_ORDER_UNLOCKED,
                    data,
                  );
                }
              } else {
                // No DI if TERMINAL, e.,g. REJECTED
                if (CubiqTS.OrderState.REJECTED !== this.getter.getSrvOrderState(refId)) {
                  let promiseArray = [
                    new Promise<boolean>((res, rej) => {
                      this.permissions
                        .determineDIInterventionPermissions(
                          CubiqTS.InterventionOperationType.APPROVE,
                          invEvent.sgcId,
                          invEvent.user,
                        )
                        .then((result: boolean) => {
                          res(result);
                        });
                    }),
                    new Promise<boolean>((res, rej) => {
                      this.permissions
                        .determineDIInterventionPermissions(
                          CubiqTS.InterventionOperationType.REJECT,
                          invEvent.sgcId,
                          invEvent.user,
                        )
                        .then((result: boolean) => {
                          res(result);
                        });
                    }),
                    this.isExternallyFilledTg(event),
                  ];
                  Promise.all(promiseArray)
                    .then(
                      (values: Array<boolean>) => {
                        // check again
                        if (CubiqTS.OrderState.REJECTED !== this.getter.getSrvOrderState(refId)) {
                          let approvePermission: boolean = values[0];
                          let rejectPermission: boolean = values[1];
                          let isExternal: boolean = values[2];
                          //
                          let tooltipTR: string = "";
                          let errorMessages =
                            event.errors != null && event.errors.errorMessages != null
                              ? event.errors.errorMessages
                              : null;
                          if (Array.isArray(errorMessages) && errorMessages.length > 0) {
                            tooltipTR = this.handleErrors(errorMessages);
                          }
                          //
                          if (approvePermission || rejectPermission) {
                            let approveButtonState = approvePermission;
                            let rejectButtonState = rejectPermission;
                            // There is an counterproductive system unlock intervention event on test43 that screws up DI controls on new entry on test43
                            // which could be related to OMS-33791, just ignore that shit
                            if (
                              !(
                                event.unlockUserId == "-1" &&
                                (this.getter.isOperationModeNew(refId) ||
                                  this.getter.isOperationModeDuplicate(refId))
                              )
                            ) {
                              approveButtonState =
                                approvePermission &&
                                (event.unlockUserId == null ||
                                  event.unlockUserId === this.settings.currentUserId) &&
                                (event.user == null || event.user === this.settings.currentUserId);
                              rejectButtonState =
                                rejectPermission &&
                                (event.unlockUserId == null ||
                                  event.unlockUserId === this.settings.currentUserId) &&
                                (event.user == null || event.user === this.settings.currentUserId);
                            }
                            let data = {
                              approval: approveButtonState,
                              rejection: rejectButtonState,
                              tooltip: tooltipTR,
                            };
                            this.outputExtendedLogging(
                              `DEV-UNLOCK: OE-Lister.onIntervention(L-297): ${activeRefId} -> APPROVAL_ALLOWED | data: ${data} | event:`,
                              event,
                            );
                            this.coordinator.cmdChangeDisplayState(
                              activeRefId,
                              OENCommand.STATE_ORDER_APPROVAL_ALLOWED,
                              data,
                            );
                            // update model state & id
                            this.setter.setSrvOrderState(refId, CubiqTS.OrderState.DI_NEEDED);
                            this.setter.updateOrderId(refId, invEvent.orderId, invEvent.orderRef);
                            // create sync event
                            let updateEvent = new OEUpdateEventModel(
                              refId,
                              CubiqTS.OrderState.DI_NEEDED,
                            );
                            updateEvent.cmdStateChange = OENCommand.STATE_ORDER_APPROVAL_ALLOWED;
                            updateEvent.data = data;
                            updateEvent.orderId = invEvent.orderId;
                            this.sync.addEventUpdateToQueue(activeRefId, updateEvent);
                          } else {
                            let cmd = isExternal
                              ? OENCommand.STATE_ORDER_APPROVAL_EXTERNAL
                              : OENCommand.STATE_ORDER_APPROVAL_INTERNAL;
                            this.outputExtendedLogging(
                              `DEV-UNLOCK: OE-Lister.onIntervention(L-310): ${activeRefId} ->  ${OENCommand[cmd]} | event:`,
                              event,
                            );
                            this.coordinator.cmdChangeDisplayState(activeRefId, cmd, {
                              tooltip: tooltipTR,
                            });
                            // update model state & id
                            this.setter.setSrvOrderState(refId, CubiqTS.OrderState.DI_NEEDED);
                            this.setter.updateOrderId(refId, invEvent.orderId, invEvent.orderRef);
                            // create sync event
                            let updateEvent = new OEUpdateEventModel(
                              refId,
                              CubiqTS.OrderState.DI_NEEDED,
                            );
                            updateEvent.cmdStateChange = cmd;
                            updateEvent.orderId = invEvent.orderId;
                            this.sync.addEventUpdateToQueue(activeRefId, updateEvent);
                          }
                        } else {
                          this.outputExtendedLogging(
                            `[INTERVENTION]: OE-Lister.onIntervention(L-357): Not processing InterventionEvent Promise as order already REJECTED | event:`,
                            event,
                          );
                        }
                      },
                      (rejection: any) => {
                        this.coordinator.cmdChangeDisplayState(
                          activeRefId,
                          OENCommand.STATE_ORDER_INT_ERROR,
                          "UI ERROR: Permission promise rejected!",
                        );
                      },
                    )
                    .catch((reason) => {
                      this.coordinator.cmdChangeDisplayState(
                        activeRefId,
                        OENCommand.STATE_ORDER_INT_ERROR,
                        "UI ERROR: Exception = " + reason,
                      );
                    });
                } else {
                  this.outputExtendedLogging(
                    `[INTERVENTION]: OE-Lister.onIntervention(L-368): Not processing InterventionEvent Promise as order already REJECTED | event:`,
                    event,
                  );
                }
              }
            } else if (invEvent.invOprType === CubiqTS.InterventionOperationType.APPROVE) {
              let logInvOprText = `Inv.OPR: (${
                invEvent.invOprType != null ? invEvent.invOprType : "N/A"
              }) = [ ${
                invEvent.invOprType != null
                  ? CubiqTS.InterventionOperationType[invEvent.invOprType]
                  : "N/A"
              } ]`;
              this.outputExtendedLogging(
                `UI-MAIN: OrderEntry@${Date.now()} [INTERVENTION]: IGNORE >>> INTERVENTION Event (msgId: ${
                  event.messageId
                }) => [${
                  event.ref
                }] | ${logInvOprText} -> Approve events handled through order-life-cycle events!`,
              );
            } else if (invEvent.invOprType === CubiqTS.InterventionOperationType.REJECT) {
              let data = event.errors;
              this.outputExtendedLogging(
                `DEV-UNLOCK: OE-Lister.onIntervention(L-333): ${activeRefId} ->  REJECTED | event:`,
                event,
              );
              this.coordinator.cmdChangeDisplayState(
                activeRefId,
                OENCommand.STATE_ORDER_REJECTED,
                data,
              );
              // update model state & id
              this.setter.setSrvOrderState(refId, CubiqTS.OrderState.REJECTED);
              this.setter.updateOrderId(refId, invEvent.orderId, invEvent.orderRef);

              // added for modify - could cause problems in order cases, but then again maybe not
              if (!this.getter.supportsMonitoringExecution(refId)) {
                setTimeout(() => {
                  // kill subscriptions after UPDATE event has been processed
                  this.destroyAllWorkerSubscriptions(refId);
                }, 500);
              }
              this.setter.removeLockForModification(refId);
              ///
              this.outputExtendedLogging(
                `DEV-UNLOCK: OE-Lister.onIntervention(L-346): ${activeRefId} ->  LOCK-INTERFACE | ActionsLock: false | event:`,
                event,
              );
              this.coordinator.cmdLockInterfaceControls(activeRefId, false);
              // create sync event
              let updateEvent = new OEUpdateEventModel(refId, CubiqTS.OrderState.REJECTED);
              updateEvent.cmdStateChange = OENCommand.STATE_ORDER_REJECTED;
              updateEvent.data = data;
              updateEvent.orderId = invEvent.orderId;
              this.sync.addEventUpdateToQueue(activeRefId, updateEvent);
            }
          } else {
            this.coordinator.cmdChangeDisplayState(
              activeRefId,
              OENCommand.STATE_ORDER_INT_ERROR,
              'UI ERROR: Invalid intervention operation type! refId: "' + invEvent.ref + '"',
            );
          }
        } else {
          //  log
          let logInvEvtText = `Inv.EVT: (${
            invEvent.invEvtType != null ? invEvent.invEvtType : "N/A"
          }) = [ ${
            invEvent.invEvtType != null ? CubiqTS.InterventionEventType[invEvent.invEvtType] : "N/A"
          } ]`;
          let logInvOprText = `Inv.OPR: (${
            invEvent.invOprType != null ? invEvent.invOprType : "N/A"
          }) = [ ${
            invEvent.invOprType != null
              ? CubiqTS.InterventionOperationType[invEvent.invOprType]
              : "N/A"
          } ]`;
          this.outputExtendedLogging(
            `UI-MAIN: OrderEntry@${Date.now()} [INTERVENTION]: IGNORE >>> INTERVENTION Event (msgId: ${
              event.messageId
            }) => [${event.ref}] | ${logInvOprText} -> No active subscription!`,
          );
        }
      }
    }
  }

  /**********************************************************************/
  /* ORDER ERROR-EVENT EVENT HANDLER */

  protected onOrderErrorEvent(event: any): void {
    if (event != null) {
      logger.out.info(
        `UI-MAIN: OrderEntry@${Date.now()} [ERROR]: ERROR Event (msgId: ${
          event.messageId
        }) RECEIVED =>  [${event.ref}] | State: (${
          event.state != null ? event.state : "N/A"
        }) = [ ${CubiqTS.OrderState[event.state]} ]`,
      ); // | Event dump: `,event)

      if (CubiqTS.RejectSubType.CANCEL_REJECT == event.rejectType) {
        // not something or OE to process
        logger.out.info(`UI-MAIN: [IGNORE]: OE doesnt process CANCEL_REJECT`);
        return;
      }
      // overwrite refId if it has a forward refId
      let refId: string = event.ref;
      if (this.sync.isTheRefIdForwarded(refId)) {
        refId = this.sync.getForwardTargetRefId(refId);
      }

      if (event != null && this.getter.has(refId)) {
        let updateEvent: OEUpdateEventModel = null;
        // update state
        if (event.state != null && typeof event.state === "number") {
          this.setter.setSrvOrderState(refId, event.state);
          updateEvent = new OEUpdateEventModel(refId, event.state);
        } else {
          this.setter.setSrvOrderState(refId, CubiqTS.OrderState.REJECTED);
          updateEvent = new OEUpdateEventModel(refId, CubiqTS.OrderState.REJECTED);
        }
        // close dialogs if any are open
        this.closeDialogIfActive(refId);
        // handle error msg
        if (event.errors && event.errors.errorMessages && event.errors.errorMessages.length > 0) {
          let errors = this.handleErrors(event.errors.errorMessages);
          let errorId = JSON.parse(event.errors.errorMessages[0]).id;

          /* Actively displayed refId is used due to problems with event.ref on root sibling where the response wont be  */

          let activeRefId = this.getter.getActivelyDisplayedRefId(refId);
          activeRefId = activeRefId != null ? activeRefId : refId;

          if (
            errorId === "order.unlocked.by.user" ||
            errorId === "html.ui.order.unlocked.by.user"
          ) {
            this.outputExtendedLogging(
              `DEV-UNLOCK: OE-Lister.onError(L-405): ${activeRefId} -> ORDER-UNLOCK | event:`,
              event,
            );
            const monitorExecution = this.getter.supportsMonitoringExecution(activeRefId);
            const displayState = monitorExecution
              ? OENCommand.STATE_MONITOR_EXECUTION
              : OENCommand.STATE_ORDER_UNLOCKED;
            this.coordinator.cmdChangeDisplayState(activeRefId, displayState, errors);
            updateEvent.cmdStateChange = displayState;
            // Commented out code below is a Hack fix for OMS-33530 which uses errorMsg to determine reject reason, which is a bad approach
            //}else if(this.determineHardCutoffError(event.errors.errorMessages)){
            //	this.coordinator.cmdChangeDisplayState(activeRefId, OENCommand.STATE_ORDER_REJECTED, {error: errors, disableEdit: true});
          } else {
            // user.abort
            this.outputExtendedLogging(
              `DEV-UNLOCK: OE-Lister.onError(L-412): ${activeRefId} -> REJECT | event:`,
              event,
            );
            this.coordinator.cmdChangeDisplayState(
              activeRefId,
              OENCommand.STATE_ORDER_REJECTED,
              errors,
            );
            updateEvent.cmdStateChange = OENCommand.STATE_ORDER_REJECTED;
          }
          // set updateEvent
          updateEvent.lockedForMod = false;
          updateEvent.lockInterface = true;
          updateEvent.lockActions = false;
          updateEvent.subscriptionLifeCycleActive = false;
          updateEvent.subscriptionInterventionActive = false;
          updateEvent.data = errors;
          this.sync.addEventUpdateToQueue(activeRefId, updateEvent);
          this.setter.stopInInitialization(activeRefId);
          // added for modify - could cause problems in order cases, but then again maybe not
          if (!this.getter.supportsMonitoringExecution(refId)) {
            this.destroyAllWorkerSubscriptions(refId);
          }
          this.setter.removeLockForModification(refId);
          ///
          this.coordinator.cmdLockInterfaceControls(activeRefId, false);
        }
      } else {
        this.outputExtendedLogging(
          `UI-MAIN: OrderEntry@${Date.now()} [ERROR]: IGNORE >>> ERROR Event (msgId: ${
            event != null ? event.messageId : "N/A"
          }) => [${event != null ? event.ref : "N/A"}] -> ${
            event != null ? "Model with that refId does not exist" : "Event data is NULL"
          }`,
        );
      }
    }
  }

  /**********************************************************************/
  /* ORDER LIFE-CYCLE EVENT HANDLER */

  protected onOrderLifeCycleEvent(event: IOrderEntryEvent): void {
    if (event instanceof Array) {
      event.forEach((element) => {
        this.scheduleLifeCycleEventProcessing(element);
      });
    } else {
      this.scheduleLifeCycleEventProcessing(event);
    }
  }

  protected scheduleLifeCycleEventProcessing(event: IOrderEntryEvent): void {
    if (
      event != null &&
      typeof event.ref === "string" &&
      typeof event.messageId === "string" &&
      event.messageId.length > 10
    ) {
      if (!this.control.lifeCycle.processorBusy) {
        this.control.lifeCycle.processorBusy = true;
        this.control.lifeCycle.processingRefId = event.ref;
        this.control.lifeCycle.processingMessageId = event.messageId;
        this.control.lifeCycle.processingTimeout = setTimeout(() => {
          if (
            event.ref === this.control.lifeCycle.processingRefId &&
            event.messageId === this.control.lifeCycle.processingMessageId
          ) {
            // log
            this.outputLifeCycleProcessorQueueUpdate(
              this.control.lifeCycle.processingRefId,
              "PROCESSING RESET",
              this.control.lifeCycle.processingMessageId,
            );
            this.resetProcessorState();
            this.processQueuedLifeCycleEvent();
          }
        }, this.control.lifeCycle.processingTimeoutLimit);
        // log
        this.outputLifeCycleProcessorQueueUpdate(event.ref, "PROCESSING START", event.messageId);
        this.processLifeCycleEvent(event).then((value) => {
          this.resetProcessorState();
          // Log
          this.outputLifeCycleProcessorQueueUpdate(
            event.ref,
            "PROCESSING COMPLETE",
            event.messageId,
          );
          this.processQueuedLifeCycleEvent();
        });
      } else {
        // log
        this.outputLifeCycleProcessorQueueUpdate(event.ref, "ADD TO QUEUE", event.messageId);
        this.control.lifeCycle.eventQueue.push(event);
      }
    } else {
      logger.out.error(
        `ERROR: oeWorkerListener.scheduleLifeCycleEventProcessing(): Invalid event %O`,
        event,
      );
    }
  }

  private processQueuedLifeCycleEvent(): void {
    if (
      Array.isArray(this.control.lifeCycle.eventQueue) &&
      this.control.lifeCycle.eventQueue.length > 0
    ) {
      let event = this.control.lifeCycle.eventQueue.shift();
      //
      this.outputLifeCycleProcessorQueueUpdate(event.ref, "GET FROM QUEUE", event.messageId);
      this.scheduleLifeCycleEventProcessing(event);
    }
  }

  private resetProcessorState(): void {
    this.control.lifeCycle.processorBusy = false;
    this.control.lifeCycle.processingRefId = null;
    this.control.lifeCycle.processingMessageId = null;
    if (this.control.lifeCycle.processingTimeout != null) {
      clearTimeout(this.control.lifeCycle.processingTimeout);
    }
  }

  protected processLifeCycleEvent(event: IOrderEntryEvent): Promise<any> {
    return new Promise<boolean>((resEntryEvent) => {
      try {
        if (event != null) {
          let rollbackEvt: boolean = false;
          if (event instanceof BmarkOrderDataEventPayload) {
            let morphed: BmarkOrderDataEventPayload = event;
            rollbackEvt = morphed.isRollbackEvent;
          }
          const subscribedForId: boolean =
            event.ref != null &&
            typeof event.ref === "string" &&
            this.isLifeCycleSubscribedForRefId(event.ref) &&
            this.getter.has(event.ref);

          if (event.rollbackEventFg) {
            if (subscribedForId) {
              this.processLifeCycleRollbackEvent(event);
              resEntryEvent(true);
            } else {
              logger.out.info(
                `owl-s - Not processing rollBack event - not subscribed to ref ${event.orderRef} - id = ${event.orderId}`,
              );
            }
          } else if (!rollbackEvt && subscribedForId) {
            // code block bellow is commented out because of https://www.bcdfx.com/ijira/browse/OMS-33343
            // -----------------------------------------------------------------------------------------
            // this can happen when an order modification is rolled back by the fixing going into HCO
            // you get an ACTIVE updated with errors that need ot be displayed
            // and the window to behave as if it was rejected
            // if(event.evtType && event.evtType === FxLifeCycleEventType.ACTIVE
            // 	&&
            // 	event.errors && event.errors.errorMessages && event.errors.errorMessages.length > 0)
            // {
            // 	this.onOrderErrorEvent(event);
            // 	resEntryEvent(true);
            // 	return;
            // }

            // log incoming event
            let stateText = `state: (${event.state != null ? event.state : "N/A"}) = [ ${
              event.state != null ? CubiqTS.OrderState[event.state] : "N/A"
            } ]`;
            let eventTypeText = `eventType: (${
              event.evtType != null ? event.evtType : "N/A"
            }) = [ ${event.evtType != null ? CubiqTS.FxLifeCycleEventType[event.evtType] : "N/A"}]`;
            logger.out.info(
              `UI-MAIN: OrderEntry@${Date.now()} [LIFE-CYCLE]: UPDATE${
                event.lockEvent ? "-LOCK" : ""
              } (msgId: ${event.messageId}) RECEIVED => [${
                event.ref
              }] | ${stateText} | ${eventTypeText}`,
            ); //

            // define refIds - activeRefId should be sent to coordinator (active may be different than event)
            let refId: string = event.ref;
            let activeRefId: string = this.getter.getActivelyDisplayedRefId(refId);
            activeRefId = activeRefId != null ? activeRefId : refId;
            let rootRefId: string = this.getter.getRootIdForNodeWithRefId(refId);

            // handle traderGroups for bmarks
            let tgcId: any = event.tgcId;
            event.tgcId =
              typeof tgcId === "string" || tgcId == null
                ? tgcId
                : Array.isArray(tgcId) && tgcId.length === 1
                ? tgcId[0]
                : tgcId;

            let deskCode: any = event.deskCode;
            event.deskCode =
              typeof deskCode === "string" || deskCode == null
                ? deskCode
                : Array.isArray(deskCode) && deskCode.length === 1
                ? deskCode[0]
                : deskCode;

            let isTgcArray: boolean = Array.isArray(event.tgcId) && event.tgcId.length > 0;
            let deskCodeString = isTgcArray
              ? Array.from(new Set(event.deskCode)).join(", ")
              : event.deskCode != null
              ? event.deskCode
              : "";

            let orderUnlockedByUser: boolean = false;
            // OMS-33463 system panic
            let orderUnlockedInModification: boolean = false;

            let tooltip: string = "";
            let errorMessages =
              event.errors != null && event.errors.errorMessages != null
                ? event.errors.errorMessages
                : null;
            if (Array.isArray(errorMessages) && errorMessages.length > 0) {
              tooltip = this.handleErrors(errorMessages);
            }

            if (event.isBmark == true) {
              // NB : if it's a bmark unlock it comes in wrong - need to process as a reject with a specific error
              // Check if future meta model wrapping the event needs both evtType and evtTypeId
              if (event.evtType == FxLifeCycleEventType.UNLOCKED) {
                event.state = CubiqTS.OrderState.REJECTED;
                orderUnlockedByUser = true;
                // OP: Fix for variation on OMS-33530 (in modification instead of acceptance oe state if no submit) so no life-cycle reject
                // Active state in life-cycle event with errors for bmark in modification means order-entry reject should be displayed
              } else if (
                event.evtType == FxLifeCycleEventType.ACTIVE &&
                event.errors != null &&
                Array.isArray(event.errors.errorMessages) &&
                event.errors.errorMessages.length > 0 &&
                this.getter.isOperationModeModify(refId)
              ) {
                event.state = CubiqTS.OrderState.REJECTED;
              }
            }

            if (event.state != null && typeof event.state === "number") {
              if (this.getter.isSubscriptionToLifeCycleEventsActive(refId)) {
                // overwrite refId if forwarded
                if (this.sync.isTheRefIdForwarded(refId)) {
                  refId = this.sync.getForwardTargetRefId(refId);
                }
                // update order id
                this.setter.updateOrderId(refId, event.orderId, event.orderRef);
                // close dialogs if any are open
                this.closeDialogIfActive(refId);
                // update state
                this.setter.setSrvOrderState(refId, event.state, false, event.txTime);

                // update privatization state from ag-grid model object
                if (event.privatiseOrder) {
                  this.setter.setModificationPrivatized(refId);
                }
                let updateEvent = new OEUpdateEventModel(refId, event.state);
                updateEvent.privatized = this.getter.isModificationPrivatized(activeRefId);

                // handle lock
                switch (event.evtType) {
                  case CubiqTS.FxLifeCycleEventType.LOCKED:
                    this.checkAndKillModifyInfo(refId, event);
                    this.setter.setLockedForModification(refId, event.user);
                    updateEvent.lockedForMod = true;
                    updateEvent.lockUserId = event.user;
                    updateEvent.lockInterface = false;
                    break;
                  case CubiqTS.FxLifeCycleEventType.UNLOCKED:
                    if (
                      this.getter.getDisplayState(refId) === OENDisplayState.SHOW_ORDER_ENTRY &&
                      event.evtType === CubiqTS.FxLifeCycleEventType.UNLOCKED &&
                      this.getter.isOperationModeModify(refId) &&
                      event.state === CubiqTS.OrderState.IN_MODIFICATION
                    ) {
                      // display unlock message and cleanup form
                      orderUnlockedInModification = true;
                    } else {
                      this.setter.removeLockForModification(refId);
                      updateEvent.lockedForMod = false;
                      updateEvent.lockInterface = false;
                    }
                    break;
                  case CubiqTS.FxLifeCycleEventType.ACTIVE:
                  case CubiqTS.FxLifeCycleEventType.PENDING:
                  case CubiqTS.FxLifeCycleEventType.SUSPENDED:
                    this.setter.removeLockForModification(refId);
                    updateEvent.lockedForMod = false;
                    updateEvent.lockInterface = false;
                    break;
                }

                if (
                  event.evtType == CubiqTS.FxLifeCycleEventType.LOCKED &&
                  event.state != CubiqTS.OrderState.IN_MODIFICATION &&
                  // need to handle these events when auto-accepted remotely
                  // for order in CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED
                  this.getter.isOrderDisplayState(
                    activeRefId,
                    OENDisplayState.SHOW_APPROVAL_EXTERNAL_MODIFY,
                  ) === false
                ) {
                  // dont change state on lock events
                  resEntryEvent(true);
                  return;
                }
                if (event.evtType == CubiqTS.FxLifeCycleEventType.FILLED_PARTIAL) {
                  // dont change state on part fill events - order state is
                  resEntryEvent(true);
                  return;
                }

                let preSubmitModifyInfo: boolean =
                  this.getter.isOperationModeModifyInfo(refId) &&
                  this.getter.isOrderDisplayState(refId, OENDisplayState.SHOW_ORDER_ENTRY);

                /***********************************************************************/
                /* PROCESS ORDER STATE */
                switch (event.state) {
                  case CubiqTS.OrderState.TRIGGERED: // for now handling for an unlock event
                    if (this.getter.has(refId)) {
                      if (
                        this.getter.getDisplayState(refId) === OENDisplayState.SHOW_ORDER_ENTRY &&
                        event.evtType === CubiqTS.FxLifeCycleEventType.UNLOCKED
                      ) {
                        let messageUnlockTriggered = null;
                        if (event.user != null) {
                          let userData: CubiqTS.UserImpl = this.lookup.userByIdNow(
                            CubiqTS.Long.fromValue(event.user),
                          );
                          if (userData != null && userData instanceof CubiqTS.UserImpl) {
                            messageUnlockTriggered = "Order Unlocked by " + userData.userName;
                          }
                          this.outputExtendedLogging(
                            `DEV-UNLOCK: OE-Lister.onLifeCycle(L-616): ${activeRefId} -> ORDER-UNLOCK | event:`,
                            event,
                          );
                          this.coordinator.cmdChangeDisplayState(
                            activeRefId,
                            OENCommand.STATE_ORDER_UNLOCKED,
                            messageUnlockTriggered,
                          );
                          this.coordinator.cmdLockInterfaceControls(activeRefId, false);
                          //
                          updateEvent.cmdStateChange = OENCommand.STATE_ORDER_UNLOCKED;
                          updateEvent.data = messageUnlockTriggered;
                          // should this be here? probably yes
                          this.destroyAllWorkerSubscriptions(refId);
                        }
                        this.setter.removeLockForModification(refId);
                        //
                        updateEvent.lockedForMod = false;
                      }
                    }
                    break;
                  // do a reject
                  case CubiqTS.OrderState.FILLED:
                  case CubiqTS.OrderState.CANCELLED:
                  case CubiqTS.OrderState.EXPIRED:
                    // Used to get around a complex cascade through case statement
                    let eventProcessed = false;
                    let oeWindowAlreadyKilled: boolean = false; // used in terminal cases (FILLED, CANCELLED, REJECTED...) to avoid killing MODIFY INFO twice

                    if (
                      this.getter.isOperationModeMonitorExecution(refId) ||
                      this.getter.monitoringExecution(refId)
                    ) {
                      // Do nothing - just looking
                      eventProcessed = true;
                    } else if (this.getter.supportsMonitoringExecution(refId)) {
                      eventProcessed = true;
                      this.coordinator.cmdChangeDisplayState(refId, OENCommand.STATE_ORDER_SUCCESS);
                      this.coordinator.cmdLockInterfaceControls(refId, false);
                    } else if (this.getter.isOrderDisplayStateTerminal(refId)) {
                      // kill order entry as per https://www.bcdfx.com/ijira/browse/OMS-25845
                      this.coordinator.cmdCloseOrderEntryScreen(refId);
                      oeWindowAlreadyKilled = true;
                    } else if (
                      this.getter.isOperationModeNew(refId) ||
                      this.getter.isOperationModeDuplicate(refId) ||
                      this.getter.isOperationModeModify(refId)
                    ) {
                      let state: string = CubiqTS.OrderState[event.state].replace(/_/g, " "); // state.toLowerCase() => Expiry Acceptance Needed
                      let orderId: string = this.getter.getOrderId(activeRefId);
                      let message: string = this.capitalizePipe.transform(
                        state.toLowerCase() +
                          ": " +
                          (orderId != null ? "(Order ID: " + orderId + ")" : "") +
                          " Managed by " +
                          deskCodeString,
                        true,
                      ); // state.toLowerCase()
                      this.coordinator.cmdChangeDisplayState(
                        activeRefId,
                        OENCommand.STATE_ORDER_REJECTED,
                        message,
                      );
                      // terminate subscriptions
                      this.destroyAllWorkerSubscriptions(refId);
                      eventProcessed = true;
                    }
                    if (!eventProcessed) {
                      this.processRejectDeletedEvent(
                        event,
                        refId,
                        activeRefId,
                        oeWindowAlreadyKilled,
                        orderUnlockedByUser,
                        updateEvent,
                      );
                    }
                    break;
                  case CubiqTS.OrderState.REJECTED:
                  case CubiqTS.OrderState.DELETED:
                    // kill modify info
                    this.processRejectDeletedEvent(
                      event,
                      refId,
                      activeRefId,
                      false,
                      orderUnlockedByUser,
                      updateEvent,
                    );
                    break;
                  case CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED:
                  case CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED:
                  case CubiqTS.OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED:
                    //this.checkAndKillModifyInfo(refId, event);
                    break;
                  // accept
                  case CubiqTS.OrderState.ACTIVE:
                    // terminate intervention event subscription
                    if (!preSubmitModifyInfo) {
                      this.destroyWorkerInterventionSubscriptionsForStrategy(refId);
                    }
                  case CubiqTS.OrderState.SAVED:
                  case CubiqTS.OrderState.PENDING:
                  case CubiqTS.OrderState.SUSPENDED:
                    if (!preSubmitModifyInfo) {
                      let messageDI: string = this.capitalizePipe.transform(
                        CubiqTS.OrderState[event.state].toLowerCase() + ": " + deskCodeString,
                        true,
                      ); // .toLowerCase()

                      let issuerRefId = this.getter.getModifyIssuerRefId(refId);
                      // if added to fix: https://www.bcdfx.com/ijira/browse/OMS-25954
                      let displayState: OENCommand = OENCommand.STATE_ORDER_SUCCESS;
                      if (
                        !this.getter.isOperationModeModifyInfo(refId) ||
                        (this.getter.isOperationModeModifyInfo(refId) && refId === issuerRefId)
                      ) {
                        if (
                          this.getter.getOperationMode(refId) == OENOperationMode.MONITOR_EXECUTION
                        ) {
                          displayState = OENCommand.STATE_MONITOR_EXECUTION;
                        }

                        this.coordinator.cmdChangeDisplayState(
                          activeRefId,
                          displayState,
                          messageDI,
                        );
                      }

                      //
                      updateEvent.cmdStateChange = displayState;
                      updateEvent.data = messageDI;

                      if (event.evtType == null) {
                        // || event.evtType === CubiqTS.FxLifeCycleEventType.ACTIVE
                        this.setter.removeLockForModification(refId);
                        updateEvent.lockedForMod = false;
                      }
                    } else {
                      logger.out.info(
                        `UI-MAIN: OrderEntry@${Date.now()} [LIFE-CYCLE]: UPDATE${
                          event.lockEvent ? "-LOCK" : ""
                        } (msgId: ${
                          event.messageId
                        }) Skipping terminate of modify info entry, modification not submitted`,
                      ); //
                    }
                    break;
                  // expiry pending - apply only for new/ duplicate
                  case CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED:
                    // if(this.getter.isOperationModeNew(refId) || this.getter.isOperationModeDuplicate(refId)){
                    this.destroyWorkerInterventionSubscriptionsForStrategy(refId);

                    // let state: string = CubiqTS.OrderState[event.state].replace(/_/g, ' '); // state.toLowerCase() => Expiry Acceptance Needed
                    let orderId: string = this.getter.getOrderId(activeRefId);
                    let message: string = this.capitalizePipe.transform(
                      "Expiry Pending" +
                        ": " +
                        (orderId != null ? "(Order ID: " + orderId + ")" : "") +
                        " Managed by " +
                        deskCodeString,
                      true,
                    ); // state.toLowerCase()

                    this.coordinator.cmdChangeDisplayState(
                      activeRefId,
                      OENCommand.STATE_ORDER_EXPIRY_PENDING,
                      message,
                    );
                    // }
                    break;
                  // acceptance needed
                  case CubiqTS.OrderState.ACCEPTANCE_NEEDED:
                  case CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED: {
                    // WARNING: interior promise handles resolution. case returns early
                    // for abort on close
                    // TODO: change the model to work with multiple tgcIds

                    this.setter.setTraderGroupCodeId(
                      refId,
                      isTgcArray ? event.tgcId[0] : event.tgcId,
                    );
                    let state: string = CubiqTS.OrderState[event.state]
                      .replace(/_/g, " ")
                      .replace("EXT", "External");
                    let messageTR: string = this.capitalizePipe.transform(
                      state.toLowerCase() + ": " + deskCodeString,
                      true,
                    ); // .toLowerCase()

                    let promiseArray = [
                      new Promise<boolean>((res, rej) => {
                        this.permissions
                          .determineAcceptanceNeededPermissions(
                            CubiqTS.AcceptanceAction.ACCEPT,
                            event.tgcId,
                            event.state,
                          )
                          .then((result: boolean) => {
                            res(result);
                          });
                      }),
                      new Promise<boolean>((res, rej) => {
                        this.permissions
                          .determineAcceptanceNeededPermissions(
                            CubiqTS.AcceptanceAction.REJECT,
                            event.tgcId,
                            event.state,
                          )
                          .then((result: boolean) => {
                            res(result);
                          });
                      }),
                      new Promise<boolean>((res, rej) => {
                        this.permissions
                          .determineAcceptanceNeededPermissions(
                            CubiqTS.AcceptanceAction.ABORT,
                            event.tgcId,
                            event.state,
                          )
                          .then((result: boolean) => {
                            res(result);
                          });
                      }),
                    ];

                    /*  Check if no rate in system (needed for CALL) */
                    let displayAcceptanceTopDown: boolean = false;
                    if (
                      event.requestedOrderType === CubiqTS.RequestOrderType.CALL &&
                      ((event.orderErrors != null && typeof event.orderErrors === "string") ||
                        (event.orderErrors instanceof CubiqTS.ErrorsImpl &&
                          Array.isArray(event.orderErrors.errorMessages) &&
                          event.orderErrors.errorMessages.length > 0))
                    ) {
                      try {
                        if (typeof event.orderErrors === "string") {
                          let errorsArray = JSON.parse(event.orderErrors);
                          if (Array.isArray(errorsArray)) {
                            errorsArray.forEach((item) => {
                              let error = JSON.parse(item);
                              if (error.id === "topside.downside.required") {
                                displayAcceptanceTopDown = true;
                              }
                            });
                          }
                        } else if (event.orderErrors instanceof CubiqTS.ErrorsImpl) {
                          for (let errorMessage of event.orderErrors.errorMessages) {
                            let parsedErrorMessage = JSON.parse(errorMessage.toString());
                            if (parsedErrorMessage.id === "topside.downside.required") {
                              displayAcceptanceTopDown = true;
                            }
                          }
                        }
                      } catch (ex) {
                        displayAcceptanceTopDown = false;
                      }
                    }

                    Promise.all(promiseArray)
                      .then(
                        (values: Array<boolean>) => {
                          let acceptPermission: boolean = values[0];
                          let rejectPermission: boolean = values[1];
                          let abortPermission: boolean = values[2];

                          // WE might have a lock coming in for a different user if we are monitoring
                          abortPermission =
                            abortPermission &&
                            (event.user == null || this.settings.currentUserId == event.user);
                          let externalGroupAndState: boolean =
                            this.lookup.determineExternalTraderGroupNow(event.tgcId) &&
                            (event.state === CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED ||
                              event.state === CubiqTS.OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED ||
                              event.state === CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED);

                          if (externalGroupAndState) {
                            let data = {
                              msg: messageTR,
                              acceptance: acceptPermission,
                              rejection: rejectPermission,
                              abort: abortPermission,
                              tooltip: tooltip,
                            };
                            this.outputExtendedLogging(
                              `DEV-UNLOCK: OE-Lister.onLifeCycle(L-839): ${activeRefId} -> ACCEPTANCE_EXTERNAL | event:`,
                              event,
                            );
                            this.coordinator.cmdChangeDisplayState(
                              activeRefId,
                              OENCommand.STATE_ORDER_ACCEPTANCE_EXTERNAL,
                              data,
                            );
                            updateEvent.cmdStateChange = OENCommand.STATE_ORDER_ACCEPTANCE_EXTERNAL;
                            updateEvent.data = data;
                          } else if (displayAcceptanceTopDown) {
                            let data = {
                              msg: messageTR,
                              acceptance: acceptPermission,
                              rejection: rejectPermission,
                              abort: abortPermission,
                              tooltip: tooltip,
                            };
                            this.outputExtendedLogging(
                              `DEV-UNLOCK: OE-Lister.onLifeCycle(L-845): ${activeRefId} -> ACCEPTANCE_CALL | event:`,
                              event,
                            );
                            this.coordinator.cmdChangeDisplayState(
                              activeRefId,
                              OENCommand.STATE_ORDER_ACCEPTANCE_CALL,
                              data,
                            );
                            updateEvent.cmdStateChange = OENCommand.STATE_ORDER_ACCEPTANCE_CALL;
                            updateEvent.data = data;
                          } else {
                            let data = {
                              msg: messageTR,
                              acceptance: acceptPermission,
                              rejection: rejectPermission,
                              abort: abortPermission,
                              tooltip: tooltip,
                            };
                            this.outputExtendedLogging(
                              `DEV-UNLOCK: OE-Lister.onLifeCycle(L-851): ${activeRefId} -> ACCEPTANCE_ALLOWED | event:`,
                              event,
                            );
                            this.coordinator.cmdChangeDisplayState(
                              activeRefId,
                              OENCommand.STATE_ORDER_ACCEPTANCE_ALLOWED,
                              data,
                            );
                            updateEvent.cmdStateChange = OENCommand.STATE_ORDER_ACCEPTANCE_ALLOWED;
                            updateEvent.data = data;
                          }
                          this.sync.addEventUpdateToQueue(refId, updateEvent);
                          resEntryEvent(true);
                        },
                        (rejection) => {
                          logger.out.error(
                            `ERROR: oeWorkerListener.doOrderEntryEventPromise(): [${activeRefId}] -> ${
                              CubiqTS.OrderState[event.state]
                            } = Could not determine acceptance permissions!`,
                          );
                          logger.out.error(
                            `ERROR: PROMISE-REJECT: oeWorkerListener.doOrderEntryEventPromise(): `,
                            rejection,
                          );
                          this.coordinator.cmdChangeDisplayState(
                            activeRefId,
                            OENCommand.STATE_ORDER_INT_ERROR,
                            "UI ERROR: Could not determine acceptance permissions!",
                          );
                          resEntryEvent(false);
                        },
                      )
                      .catch((exception) => {
                        logger.out.error(
                          `ERROR: oeWorkerListener.doOrderEntryEventPromise(): [${activeRefId}] -> ${
                            CubiqTS.OrderState[event.state]
                          } = Could not determine acceptance permissions!`,
                        );
                        logger.out.error(
                          `ERROR: PROMISE-EXCEPTION: oeWorkerListener.doOrderEntryEventPromise(): `,
                          exception,
                        );
                        this.coordinator.cmdChangeDisplayState(
                          activeRefId,
                          OENCommand.STATE_ORDER_INT_ERROR,
                          "UI ERROR: Could not determine acceptance permissions",
                        );
                        resEntryEvent(false);
                      });
                    // WARNING: interior promise handles resolution. case returns early
                    return;
                  }
                  case CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED: {
                    // WARNING: interior promise handles resolution. case returns early
                    let command = OENCommand.STATE_ORDER_EXT_MODIFY_OUT_ACCEPTANCE;
                    let state = CubiqTS.OrderState[event.state]
                      .replace(/_/g, " ")
                      .replace("EXT", "External");
                    let messageTR = this.capitalizePipe.transform(
                      state.toLowerCase() + ": " + deskCodeString,
                      true,
                    );

                    Promise.all([
                      new Promise<boolean>((res, rej) => {
                        this.permissions
                          .determineAcceptanceNeededPermissions(
                            CubiqTS.AcceptanceAction.ACCEPT,
                            event.tgcId,
                            event.state,
                          )
                          .then((result: boolean) => {
                            res(result);
                          });
                      }),
                      new Promise<boolean>((res, rej) => {
                        this.permissions
                          .determineAcceptanceNeededPermissions(
                            CubiqTS.AcceptanceAction.REJECT,
                            event.tgcId,
                            event.state,
                          )
                          .then((result: boolean) => {
                            res(result);
                          });
                      }),
                    ])
                      .then(
                        (values: Array<boolean>) => {
                          let acceptPermission: boolean = values[0];
                          let rejectPermission: boolean = values[1];
                          let data = {
                            msg: messageTR,
                            acceptance: acceptPermission,
                            rejection: rejectPermission,
                            tooltip: tooltip,
                          };
                          this.outputExtendedLogging(
                            `DEV-UNLOCK: OE-Lister.onLifeCycle(L-8591): ${activeRefId} -> ${OENCommand[command]} | event:`,
                            event,
                          );
                          this.coordinator.cmdChangeDisplayState(activeRefId, command, data);
                          updateEvent.cmdStateChange = command;
                          updateEvent.data = data;
                          logger.out.info(
                            `${this.constructor.name}. OENCommand.STATE_ORDER_EXT_MODIFY_OUT_ACCEPTANCE;`,
                          );
                          this.sync.addEventUpdateToQueue(refId, updateEvent);
                          resEntryEvent(true);
                        },
                        (rejection) => {
                          logger.out.error(
                            `ERROR: oeWorkerListener.doOrderEntryEventPromise(): [${activeRefId}] -> ${
                              CubiqTS.OrderState[event.state]
                            } = Could not determine acceptance permissions!`,
                          );
                          logger.out.error(
                            `ERROR: PROMISE-REJECT: oeWorkerListener.doOrderEntryEventPromise(): `,
                            rejection,
                          );
                          this.coordinator.cmdChangeDisplayState(
                            activeRefId,
                            OENCommand.STATE_ORDER_INT_ERROR,
                            "UI ERROR: Could not determine acceptance permissions!",
                          );
                          resEntryEvent(false);
                        },
                      )
                      .catch((exception) => {
                        logger.out.error(
                          `ERROR: oeWorkerListener.doOrderEntryEventPromise(): [${activeRefId}] -> ${
                            CubiqTS.OrderState[event.state]
                          } = Could not determine acceptance permissions!`,
                        );
                        logger.out.error(
                          `ERROR: PROMISE-EXCEPTION: oeWorkerListener.doOrderEntryEventPromise(): `,
                          exception,
                        );
                        this.coordinator.cmdChangeDisplayState(
                          activeRefId,
                          OENCommand.STATE_ORDER_INT_ERROR,
                          "UI ERROR: Could not determine acceptance permissions!",
                        );
                        resEntryEvent(false);
                      });
                    // WARNING: interior promise handles resolution. case returns early
                    return;
                  }
                  case CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED:
                    if (this.isEntryWindowClosable(refId)) {
                      this.coordinator.cmdCloseOrderEntryScreen(refId);
                      // display notification for modify info
                      if (this.getter.isOperationModeModifyInfo(refId)) {
                        let orderId = this.getter.getOrderIdForRefIdOrRoot(refId);
                        let orderRef = this.getter.getOrderRef(refId);
                        this.notifications.sendErrorNotification(
                          OENNotificationType.ERROR_MODIFY_INFO_ACTIONED,
                          null,
                          orderId,
                          orderRef,
                        );
                      }
                    } else {
                      let command = OENCommand.STATE_ORDER_MODIFY_OUT_ACCEPTANCE;
                      //logger.out.debug(`DEV-TEST: Order refId '${refId}':  STATE CHANGE => '${CubiqTS.OrderState[event.state]}' = LOCKED for modification.`);
                      this.coordinator.cmdChangeDisplayState(activeRefId, command, {
                        tooltip: tooltip,
                        abort: this.settings.currentUserId == event.user,
                      });
                      updateEvent.cmdStateChange = command;
                    }
                    break;
                  case CubiqTS.OrderState.IN_MODIFICATION: {
                    // Auto Acceptance need addEventAnytime set to true
                    let addEventAnytime = true;
                    if (
                      this.getter.monitoringExecution(refId) ||
                      (!this.getter.isOperationModeModify(refId) &&
                        this.settings.currentUserId !== event.user)
                    ) {
                      // Do nothing - just looking - however we need toensure our view is reset so that
                      // it's not showing prior state e.g. EXT_ACCEPTANCE
                      logger.out.warn(
                        `oe-Worker - got In Modification for order by different user ${event.user}`,
                      );
                      this.coordinator.cmdChangeDisplayState(
                        activeRefId,
                        OENCommand.STATE_ORDER_MODIFICATION_EXTERNAL,
                        event,
                      );
                    }
                    // WARNING: interior promise handles resolution. case returns early
                    else if (this.isEntryWindowClosable(refId)) {
                      this.coordinator.cmdCloseOrderEntryScreen(refId);
                      // display notification for modify info
                      if (this.getter.isOperationModeModifyInfo(refId)) {
                        let orderId = this.getter.getOrderIdForRefIdOrRoot(refId);
                        let orderRef = this.getter.getOrderRef(refId);
                        this.notifications.sendErrorNotification(
                          OENNotificationType.ERROR_MODIFY_INFO_ACTIONED,
                          null,
                          orderId,
                          orderRef,
                        );
                      }
                      this.sync.addEventUpdateToQueue(refId, updateEvent, addEventAnytime);
                      logger.out.info(
                        `${this.constructor.name}. OENCommand.STATE_ORDER_IN_MODIFICATION;`,
                      );
                      resEntryEvent(true);
                    } else if (this.getter.isInInitialization(refId)) {
                      updateEvent.cmdStateChange = OENCommand.STATE_ORDER_IN_MODIFICATION;
                      this.sync.addEventUpdateToQueue(refId, updateEvent, addEventAnytime);
                      resEntryEvent(true);
                    } else if (orderUnlockedInModification) {
                      let messageUnlocked = null;
                      this.lookup.userById(CubiqTS.Long.fromValue(event.user)).then(
                        (userData: CubiqTS.UserImpl) => {
                          if (userData != null && userData instanceof CubiqTS.UserImpl) {
                            messageUnlocked = "Order Unlocked by " + userData.userName;
                          }
                          this.setter.removeLockForModification(refId);
                          if (this.getter.supportsMonitoringExecution(refId)) {
                            // Order has been unlocked so we need to refresh for monitoring
                            this.channelsService.refreshOrderStrategy(event.ref, event.orderId);
                          } else {
                            this.coordinator.cmdChangeDisplayState(
                              activeRefId,
                              OENCommand.STATE_ORDER_UNLOCKED,
                              {
                                message: messageUnlocked,
                                hideDuplicate: false,
                                hideEdit: true,
                              },
                            );
                            this.coordinator.cmdLockInterfaceControls(activeRefId, false);
                            updateEvent.cmdStateChange = OENCommand.STATE_ORDER_UNLOCKED;
                            updateEvent.data = messageUnlocked;
                            updateEvent.lockedForMod = false;
                            this.destroyAllWorkerSubscriptions(refId);
                            this.sync.addEventUpdateToQueue(refId, updateEvent, addEventAnytime);
                          }
                          resEntryEvent(true);
                        },
                        (err) => {
                          logger.out.error(
                            `ERROR: oeWorkerListener.doOrderEntryEventPromise(): [${activeRefId}] -> ${
                              CubiqTS.OrderState[event.state]
                            } = Failed to lookup user '${event.user}'`,
                            err,
                          );
                          resEntryEvent(false);
                        },
                      );
                    } else if (
                      this.getter.getOperationMode(refId) == OENOperationMode.MONITOR_EXECUTION
                    ) {
                      // If we're monitoring, make sure we don't go into edit state
                      this.coordinator.cmdChangeDisplayState(
                        refId,
                        OENCommand.STATE_MONITOR_EXECUTION,
                      );
                      this.sync.addEventUpdateToQueue(refId, updateEvent, addEventAnytime);
                      resEntryEvent(true);
                    } else {
                      // Don't change anything if we are middle of Tolerance check or other internal display state
                      const currentDisplayState = this.getter.getDisplayState(refId);
                      let newDisplayState: OENDisplayState;
                      switch (currentDisplayState) {
                        case OENDisplayState.SHOW_TOLERANCE:
                        case OENDisplayState.SHOW_ORDER_ENTRY:
                        case OENDisplayState.SHOW_ABORTING:
                          newDisplayState = currentDisplayState;
                          break;
                        default:
                          newDisplayState = OENDisplayState.SHOW_ORDER_ENTRY;
                          break;
                      }
                      // Don't want to change/re-evaluate the state if we're in tolerance/abort mode
                      if (newDisplayState === OENDisplayState.SHOW_ORDER_ENTRY) {
                        updateEvent.cmdStateChange = OENCommand.STATE_ORDER_IN_MODIFICATION;
                        this.coordinator.cmdChangeDisplayState(
                          refId,
                          OENCommand.STATE_ORDER_IN_MODIFICATION,
                        );
                        this.sync.addEventUpdateToQueue(refId, updateEvent, addEventAnytime);
                      }
                      resEntryEvent(true);
                    }
                    // WARNING: interior promise handles resolution. case returns early
                    return;
                  }
                  case CubiqTS.OrderState.DI_NEEDED: {
                    // WARNING: interior promise handles resolution. case returns early

                    let promiseArrayDI = [
                      new Promise<boolean>((res, rej) => {
                        this.permissions
                          .determineDIInterventionPermissions(
                            CubiqTS.InterventionOperationType.APPROVE,
                            event.sgcId,
                            event.user,
                          )
                          .then((result: boolean) => {
                            res(result);
                          });
                      }),
                      new Promise<boolean>((res, rej) => {
                        this.permissions
                          .determineDIInterventionPermissions(
                            CubiqTS.InterventionOperationType.REJECT,
                            event.sgcId,
                            event.user,
                          )
                          .then((result: boolean) => {
                            res(result);
                          });
                      }),
                      this.isExternallyFilledTg(event),
                    ];
                    Promise.all(promiseArrayDI)
                      .then(
                        (values: Array<boolean>) => {
                          let approvePermission: boolean = values[0];
                          let rejectPermission: boolean = values[1];
                          let isExternal: boolean = values[2];
                          //
                          if (approvePermission || rejectPermission) {
                            let approveButtonState = approvePermission; // && ( event.unlockUserId == null || event.unlockUserId === this.currentUserId);
                            let rejectButtonState = rejectPermission; // && (event.unlockUserId == null || event.unlockUserId === this.currentUserId);
                            let data = {
                              approval: approveButtonState,
                              rejection: rejectButtonState,
                              tooltip: tooltip,
                            };
                            this.coordinator.cmdChangeDisplayState(
                              activeRefId,
                              OENCommand.STATE_ORDER_APPROVAL_ALLOWED,
                              data,
                            );
                            // create sync event
                            updateEvent.cmdStateChange = OENCommand.STATE_ORDER_APPROVAL_ALLOWED;
                            updateEvent.data = data;
                          } else {
                            let cmd = isExternal
                              ? OENCommand.STATE_ORDER_APPROVAL_EXTERNAL
                              : OENCommand.STATE_ORDER_APPROVAL_INTERNAL;
                            this.coordinator.cmdChangeDisplayState(activeRefId, cmd, {
                              tooltip: tooltip,
                            });
                            // create sync event
                            updateEvent.cmdStateChange = cmd;
                            updateEvent.orderId = event.orderId;
                          }
                          this.sync.addEventUpdateToQueue(refId, updateEvent);
                          resEntryEvent(true);
                        },
                        (rejection: any) => {
                          logger.out.error(
                            `ERROR: oeWorkerListener.doOrderEntryEventPromise(): [${activeRefId}] -> ${
                              CubiqTS.OrderState[event.state]
                            } = Could not determine acceptance permissions!`,
                          );
                          logger.out.error(
                            `ERROR: PROMISE-REJECT: oeWorkerListener.doOrderEntryEventPromise(): `,
                            rejection,
                          );
                          this.coordinator.cmdChangeDisplayState(
                            activeRefId,
                            OENCommand.STATE_ORDER_INT_ERROR,
                            "UI ERROR: Could not determine acceptance permissions!",
                          );
                          resEntryEvent(false);
                        },
                      )
                      .catch((exception) => {
                        logger.out.error(
                          `ERROR: oeWorkerListener.doOrderEntryEventPromise(): [${activeRefId}] -> ${
                            CubiqTS.OrderState[event.state]
                          } = Could not determine acceptance permissions!`,
                        );
                        logger.out.error(
                          `ERROR: PROMISE-EXCEPTION: oeWorkerListener.doOrderEntryEventPromise(): `,
                          exception,
                        );
                        this.coordinator.cmdChangeDisplayState(
                          activeRefId,
                          OENCommand.STATE_ORDER_INT_ERROR,
                          "UI ERROR: Could not determine acceptance permissions!",
                        );
                        resEntryEvent(false);
                      });
                    // WARNING: interior promise handles resolution. case returns early
                    return;
                  }
                  case CubiqTS.OrderState.MIGRATION_ACCEPTANCE_NEEDED:
                    logger.out.warn(`owl-s Order in Migration Acceptance`);
                    break;
                  default:
                    logger.out.error(
                      `ERROR: oeWorkerListener.onOrderEntryEvent(): Received an event ${
                        CubiqTS.OrderState[event.state]
                      } for a model ${refId} of unhandled state ${event.state}`,
                    );
                    break;
                }

                // Are all operations finished and we're now in monitoring mode?
                if (
                  this.getter.monitoringExecution(refId) &&
                  this.getter.getDisplayState(refId) !== OENDisplayState.SHOW_MONITOR_EXECUTION
                ) {
                  logger.out.info("oe-wls: Setting operation mode to Monitoring Execution");
                  this.coordinator.cmdMonitorExecution(refId);
                }

                this.sync.addEventUpdateToQueue(refId, updateEvent);
                resEntryEvent(true);
              } else {
                this.outputExtendedLogging(
                  `UI-MAIN: OrderEntry@${Date.now()}: IGNORE >>> UPDATE${
                    event.lockEvent ? "-LOCK" : ""
                  } Event (msgId: ${event.messageId}) => [${event.ref}] | state: (${
                    event.state != null ? event.state : "N/A"
                  }) = [ ${
                    event.state != null ? CubiqTS.OrderState[event.state] : "NO"
                  } ] -> No active subscription!`,
                );
                resEntryEvent(true);
              }
            } else if ((event as any).xferEventType == null) {
              this.coordinator.cmdChangeDisplayState(
                refId,
                OENCommand.STATE_ORDER_INT_ERROR,
                'UI ERROR: Invalid order event operation! state = "' + event.state + '"',
              );
              resEntryEvent(false);
            } else {
              resEntryEvent(true); // Review TEST: comment out this line and do a Transfer to test the life-cycle queue fallback
            }
          } else {
            resEntryEvent(true);
          }
        } else {
          resEntryEvent(false);
        }
      } catch (err) {
        logger.out.error(`ERROR: EXCEPTION: oeWorkerListener.doOrderEntryEventPromise(): `, err);
        resEntryEvent(false);
      }
    });
  }

  private processRejectDeletedEvent(
    event: IOrderEntryEvent,
    refId: string,
    activeRefId: string,
    oeWindowAlreadyKilled: boolean,
    orderUnlockedByUser: boolean,
    updateEvent: OEUpdateEventModel,
  ) {
    if (!oeWindowAlreadyKilled) {
      // if its already killed, dont kill it again, that's is just cruel (plus there is an unwanted notification)!
      this.checkAndKillModifyInfo(refId, event);
    }

    this.setter.removeLockForModification(refId);
    let errorMapped: boolean = false;
    try {
      let errorMessages =
        event.errors != null && event.errors.errorMessages != null
          ? event.errors.errorMessages
          : null;
      let errors: string;
      if (errorMessages instanceof Array && errorMessages.length > 0) {
        errors = this.handleErrors(errorMessages);
        if (this.determineHardCutoffError(errorMessages)) {
          this.coordinator.cmdChangeDisplayState(
            activeRefId,
            OENCommand.STATE_ORDER_REJECTED_HCO_ERROR,
            errors,
          );
        } else {
          this.coordinator.cmdChangeDisplayState(
            activeRefId,
            OENCommand.STATE_ORDER_REJECTED,
            errors,
          );
        }
      } else if (orderUnlockedByUser) {
        errors = this.processBmarkUnlock(event, updateEvent, errors, activeRefId);
      } else {
        this.coordinator.cmdChangeDisplayState(
          activeRefId,
          OENCommand.STATE_ORDER_REJECTED,
          errors,
        );
        updateEvent.cmdStateChange = OENCommand.STATE_ORDER_REJECTED;
        updateEvent.data = errors;
      }

      errorMapped = true;
    } catch (ex) {
      errorMapped = false;
    }

    if (!errorMapped) {
      // if added to avoid overwriteing the error message
      if (
        this.getter.has(activeRefId) &&
        this.getter.getDisplayState(activeRefId) !== OENDisplayState.SHOW_REJECTED
      ) {
        this.coordinator.cmdChangeDisplayState(activeRefId, OENCommand.STATE_ORDER_REJECTED);
      }
      updateEvent.cmdStateChange = OENCommand.STATE_ORDER_REJECTED;
    }
    // terminate subscriptions
    // let tg = this.getter.getTraderGroup(refId);
    // OP: not sure if this is needed, result the same
    /* 	if (tg instanceof CubiqTS.TradingGroupImpl && tg.isExternallyFilledFg) {
                            this.syncDestroyWorkerInterventionSubscriptionsForStrategy(refId);
                            this.syncDestroyWorkerLifeCycleSubscriptionsForStrategy(refId);
                        } else { */
    if (!this.getter.supportsMonitoringExecution(refId)) {
      this.destroyAllWorkerSubscriptions(refId);
    }
    //}

    // this.setter.setSubscriptionToLifeCycleEventsActive
    this.coordinator.cmdLockInterfaceControls(activeRefId, false);

    // updateEvent update
    updateEvent.lockedForMod = false;
    updateEvent.lockInterface = true;
    updateEvent.lockActions = false;
    updateEvent.subscriptionLifeCycleActive = false;
    updateEvent.subscriptionInterventionActive = false;
  }

  processLifeCycleRollbackEvent(event: IOrderEntryEvent) {
    switch (event.state) {
      case CubiqTS.OrderState.PENDING:
      case CubiqTS.OrderState.ACTIVE:
        if (this.getter.supportsMonitoringExecution(event.ref)) {
          this.channelsService.refreshOrderStrategy(event.ref, event.orderId);
        }
        break;
      default:
        break;
    }
  }

  /**
   * Can an open entry window ticket be closed for the specified ref
   * @param refId
   * @returns
   */
  private isEntryWindowClosable(refId: string) {
    return (
      this.getter.has(refId) &&
      !this.getter.supportsMonitoringExecution(refId) &&
      (!this.getter.isOperationModeModify(refId) ||
        (this.getter.isOrderDisplayStateTerminal(refId) && !this.getter.isInInitialization(refId)))
    );
  }

  private processBmarkUnlock(
    event: IOrderEntryEvent,
    updateEvent: OEUpdateEventModel,
    errors: string,
    activeRefId: string,
  ) {
    if (event.errors == null) {
      event.errors = new CubiqTS.ErrorsImpl();
      let userData = this.lookup.userByIdNow(CubiqTS.Long.fromValue(event.user));

      if (userData != null && userData instanceof CubiqTS.UserImpl) {
        let error = {
          id: "html.ui.order.unlocked.by.user",
          args: [userData.userName, event.orderRef],
        };
        updateEvent.data = [new CubiqTS.Message(JSON.stringify(error))];
        updateEvent.cmdStateChange = OENCommand.STATE_ORDER_UNLOCKED;

        errors = this.handleErrors(updateEvent.data);
      }
    } else {
      errors = this.handleErrors(event.errors.errorMessages);
    }
    this.outputExtendedLogging(
      `DEV-UNLOCK: OE-Lister.onLifeCycle(L-691): ${activeRefId} -> ORDER-UNLOCK | event:`,
      event,
    );

    this.coordinator.cmdChangeDisplayState(activeRefId, OENCommand.STATE_ORDER_UNLOCKED, errors);

    return errors;
  }

  /**********************************************************************/
  /* SCRATCHPAD EVENT HANDLER */

  protected onScratchpadSaveEvent(event: any): void {
    if (event instanceof Array) {
      event.forEach((element) => {
        this.doScratchpadSaveEvent(element);
      });
    } else {
      this.doScratchpadSaveEvent(event);
    }
  }

  protected doScratchpadSaveEvent(event: any): void {
    if (event != null) {
      if (event.ref != null && typeof event.ref === "string" && this.getter.has(event.ref)) {
        // log incoming event
        let stateText = `state: (${event.state != null ? event.state : "N/A"}) = [ ${
          event.state != null ? CubiqTS.OrderState[event.state] : "N/A"
        } ]`;
        logger.out.info(
          `UI-MAIN: OrderEntry@${Date.now()} [SCRATCH-PAD]: UPDATE (msgId: ${
            event.messageId
          }) RECEIVED => [${event.ref}] | ${stateText}`,
        ); //

        // define refIds - activeRefId should be sent to coordinator (active may be different than event)
        let refId: string = event.ref;
        let activeRefId: string = this.getter.getActivelyDisplayedRefId(refId);
        activeRefId = activeRefId != null ? activeRefId : refId;
        let rootRefId: string = this.getter.getRootIdForNodeWithRefId(refId);

        if (event.state != null && typeof event.state === "number") {
          if (this.getter.isSubscriptionToScratchpadEventsActive(refId)) {
            // overwrite refId if forwarded
            if (this.sync.isTheRefIdForwarded(refId)) {
              refId = this.sync.getForwardTargetRefId(refId);
            }
            // update order id
            this.setter.updateOrderId(refId, event.orderId, event.orderRef);
            // close dialogs if any are open
            this.closeDialogIfActive(refId);
            // handle state
            this.setter.setSrvOrderState(refId, event.state);
            let updateEvent = new OEUpdateEventModel(refId, event.state);

            // 			// handle state
            switch (event.state) {
              case CubiqTS.OrderState.SAVED:
                let messageDI: string = this.capitalizePipe.transform(
                  CubiqTS.OrderState[event.state].toLowerCase(),
                  true,
                ); // .toLowerCase()
                this.coordinator.cmdChangeDisplayState(
                  activeRefId,
                  OENCommand.STATE_ORDER_SUCCESS,
                  messageDI,
                );
                updateEvent.cmdStateChange = OENCommand.STATE_ORDER_SUCCESS;
                updateEvent.data = messageDI;
                // terminate subscriptions
                this.destroyWorkerScratchpadSubscriptionsForStrategy(refId);
                this.coordinator.cmdLockInterfaceControls(activeRefId, false);
                // updateEvent update
                updateEvent.lockInterface = true;
                updateEvent.lockActions = false;
                updateEvent.subscriptionScratchpadActive = false;
                break;
              case CubiqTS.OrderState.REJECTED:
                let errorMapped: boolean = false;
                try {
                  let errorMessages =
                    event.errors != null && event.errors.errorMessages != null
                      ? event.errors.errorMessages
                      : null;
                  if (errorMessages instanceof Array && errorMessages.length > 0) {
                    let errors = this.handleBulkRejectedErrors(errorMessages);
                    let errorId = errorMessages[0].jsonMessage
                      ? JSON.parse(errorMessages[0].jsonMessage).id
                      : JSON.parse(errorMessages[0].toString()).id;
                    this.coordinator.cmdChangeDisplayState(
                      activeRefId,
                      OENCommand.STATE_ORDER_REJECTED,
                      errors,
                    );
                    updateEvent.cmdStateChange = OENCommand.STATE_ORDER_REJECTED;
                    updateEvent.data = errors;
                    errorMapped = true;
                  }
                } catch (ex) {
                  errorMapped = false;
                }

                if (!errorMapped) {
                  // if added to avoid overwriteing the error message
                  if (
                    this.getter.has(activeRefId) &&
                    this.getter.getDisplayState(activeRefId) !== OENDisplayState.SHOW_REJECTED
                  ) {
                    this.coordinator.cmdChangeDisplayState(
                      activeRefId,
                      OENCommand.STATE_ORDER_REJECTED,
                    );
                  }
                  updateEvent.cmdStateChange = OENCommand.STATE_ORDER_REJECTED;
                }
                // terminate subscriptions
                this.destroyAllWorkerSubscriptions(refId);
                this.setter.removeLockForModification(refId);
                this.coordinator.cmdLockInterfaceControls(activeRefId, false);

                // updateEvent update
                updateEvent.lockedForMod = false;
                updateEvent.lockInterface = true;
                updateEvent.lockActions = false;
                updateEvent.subscriptionLifeCycleActive = false;
                updateEvent.subscriptionInterventionActive = false;
                break;
              default:
                logger.out.error(
                  `ERROR: _oeWorker.doScratchpadSaveEvent(): Received an event ${
                    CubiqTS.OrderState[event.state]
                  } for a model ${refId} of unhandled state ${event.state}`,
                );
                break;
            }
            this.sync.addEventUpdateToQueue(refId, updateEvent);
          } else {
            this.outputExtendedLogging(
              `UI-MAIN: OrderEntry@${Date.now()}: IGNORE >>> UPDATE Event (msgId: ${
                event.messageId
              }) => [${event.ref}] | state: (${event.state != null ? event.state : "N/A"}) = [ ${
                event.state != null ? CubiqTS.OrderState[event.state] : "NO"
              } ] -> No active subscription!`,
            );
          }
        } else {
          this.coordinator.cmdChangeDisplayState(
            refId,
            OENCommand.STATE_ORDER_INT_ERROR,
            'UI ERROR: Invalid order event operation! state = "' + event.state + '"',
          );
        }
      }
    }
  }

  /**********************************************************************/
  /* CLEAR EVENT HANDLER */

  public onOrderEntryClearEvent(event: IOrderEntryEvent): void {
    if (event != null) {
      if (
        this.getter.isOperationModeModify(event.ref) &&
        !this.getter.monitoringExecution(event.ref)
      ) {
        //logger.out.warn(`WARNING: Order refId: '${event.ref}' opened for MODIFICATION, closing window!`);
        this.coordinator.cmdCloseOrderEntryScreen(event.ref);
        this.outputExtendedLogging(
          `UI-MAIN: OrderEntry@${Date.now()} [CLEAR]: APPLIED >>> CLEAR Event (msgId: ${
            event.messageId
          }) => [${event.ref}] -> Order entry screen closed.`,
        );
      } else {
        this.outputExtendedLogging(
          `UI-MAIN: OrderEntry@${Date.now()} [CLEAR]: IGNORE >>> CLEAR Event (msgId: ${
            event.messageId
          }) => [${event.ref}]`,
        );
      }
    }
  }

  /**
   * Handle the auto-migration of a single order - the event contains the order and the source xfer event
   * that signalled the migration
   * @param event
   */
  public onOrderMigrationEvent(event: IOrderMigrationEvent): void {
    if (event?.order?.clOrdID && event.xferEvent) {
      const refId: string = event.order.clOrdID;
      const subscribedForId = this.isLifeCycleSubscribedForRefId(refId) && this.getter.has(refId);

      if (subscribedForId) {
        logger.out.info(`owl-s - Got Order Migration Event for ${event.order.orderRef}`);
        // We are monitoring this order so need to stop what we're doing as order in in migration acceptance flow now
        this.setter.setSrvOrderState(refId, event.order.orderState);
        if (
          this.getter.supportsMonitoringExecution(refId) &&
          this.getter.getDisplayState(refId) !== OENDisplayState.SHOW_MONITOR_EXECUTION
        ) {
          logger.out.info("oe-wls: Setting operation mode to Monitoring Execution");
          this.coordinator.cmdMonitorExecution(refId);
          this.modelOperations.processExternalTransfer(event.order);
        } else {
          // For limits, we just put up a message and allow the order to be copied
          const eventPayload = {
            hideEdit: true, // Used by oe-panel-actions
            hideDuplicate: false, // Used by oe-panel-actions
            message: event.xferEvent.comment, // Used by oe-panel-notifications
          };
          this.coordinator.cmdChangeDisplayState(
            refId,
            OENCommand.STATE_ORDER_UNLOCKED,
            eventPayload,
          );
        }
      }
    }
  }

  /**********************************************************************/
  /* HELPERS */

  private isExternallyFilledTg(event: IOrderEntryEvent): Promise<boolean> {
    return new Promise<boolean>((res) => {
      // let id = event.deskCode != null ? event.deskCode : event.o
      // this.tgLookup.getTraderGroupForSid(id)
      if (event instanceof BmarkOrderDataEventPayload) {
        let bmarkOrderData = event.bmarkOrderData;
        if (bmarkOrderData != null) {
          let traderGroupId =
            bmarkOrderData.deskId != null ? bmarkOrderData.deskId : bmarkOrderData.lastOwnerGroupId;
          if (traderGroupId != null) {
            this.tgLookup.getTraderGroupForSid(traderGroupId.toString()).then((tg) => {
              if (tg != null) {
                res(tg.isExternallyFilledFg);
              } else {
                res(false);
              }
            });
          } else {
            res(false);
          }
        } else {
          res(false);
        }
      } else {
        res(false);
      }
    });
  }

  private checkAndKillModifyInfo(refId: string, event: IOrderEntryEvent): void {
    if (event != null && this.getter.isOperationModeModifyInfo(refId)) {
      //logger.out.warn(`WARNING: Order refId: '${event.ref}' opened in MODIFY_INFO, closing window!`);
      this.coordinator.cmdCloseOrderEntryScreen(event.ref);
      //
      let orderId = this.getter.getOrderIdForRefIdOrRoot(refId);
      if (
        event.state === CubiqTS.OrderState.EXPIRED ||
        event.state === CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED ||
        event.state === CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED
      ) {
        this.notifications.sendErrorNotification(
          OENNotificationType.ERROR_MODIFY_INFO_EXPIRED,
          null,
          orderId,
          event.orderRef,
        );
      } else {
        this.notifications.sendErrorNotification(
          OENNotificationType.ERROR_MODIFY_INFO_ACTIONED,
          null,
          orderId,
          event.orderRef,
        );
      }
    }
  }

  private closeDialogIfActive(refId: string): void {
    let rootRefId: string = this.getter.getRootIdForNodeWithRefId(refId);
    // for now only dialog close that needs to be handled is in SHOW_PROCESSING state
    if (
      this.getter.has(rootRefId) &&
      this.getter.getDisplayState(rootRefId) === OENDisplayState.SHOW_PROCESSING
    ) {
      this.dialogs.closeDialogsRefs(rootRefId);
    }
  }

  /**********************************************************************/
  /* ALL subscriptions  */

  // added to kill scratchpad as well, but private because if other services kill subscriptions it will be easier to trace without using this method.
  private destroyAllWorkerSubscriptions(refId: string): void {
    if (typeof refId === "string") {
      this.destroyWorkerInterventionSubscriptionsForStrategy(refId);
      this.destroyWorkerLifeCycleSubscriptionsForStrategy(refId);
      this.destroyWorkerScratchpadSubscriptionsForStrategy(refId);
    }
  }

  /**********************************************************************/
  /* Order Life-Cycle subscription  */

  public isLifeCycleSubscribedForRefId(refId: string): boolean {
    return typeof refId === "string" && this.lifeCycleSubscribedRefIds.has(refId);
  }

  // INITIALIZE - SUBSCRIBE
  public initializeWorkerLifeCycleSubscriptionsForStrategy(refId: string): void {
    let strategyNodes: Array<string> = this.getter.getStrategyNodesListRoot(refId);
    strategyNodes.forEach((nodeRefId) => {
      this.initializeWorkerLifeCycleSubscriptionsForSingleNode(nodeRefId);
    });
  }

  private initializeWorkerLifeCycleSubscriptionsForSingleNode(refId: string): void {
    if (this.getter.has(refId) && !this.getter.isSubscriptionToLifeCycleEventsActive(refId)) {
      this.lifeCycleSubscribedRefIds.add(refId);
      //logger.out.warn(`INFO Order entry subscription ADD for ${refId} | list: [${[...this.lifeCycleSubscribedRefIds].join(' ,')}]`)
      this.setter.setLifeCycleSubscriptionStatus(refId, true);
      this.orderLookup.addOrderEntryReference(refId);

      //logger.out.info(`DEV-TEST: LIFE-CYCLE Subscription STARTED for: ${refId} | size = ${this.getter.determineLifeCycleActiveSubscriptions()}`);
    }
  }

  // DESTROY - UNSUBSCRIBE
  public destroyWorkerLifeCycleSubscriptionsForStrategy(refId: string): void {
    let strategyNodes: Array<string> = this.getter.getStrategyNodesListRoot(refId);
    strategyNodes.forEach((nodeRefId: string) => {
      this.destroyWorkerLifeCycleSubscriptionsForSingleNode(nodeRefId);
    });
  }

  private destroyWorkerLifeCycleSubscriptionsForSingleNode(refId: string): void {
    if (this.getter.isSubscriptionToLifeCycleEventsActive(refId)) {
      this.lifeCycleSubscribedRefIds.delete(refId);
      //logger.out.warn(`INFO Order entry subscription DELETE for ${refId} | list: [${[...this.lifeCycleSubscribedRefIds].join(' ,')}]`);
      if (this.sync.isTheRefIdForwarded(refId)) {
        this.sync.unregisterForward(refId);
      }
      this.setter.setLifeCycleSubscriptionStatus(refId, false);
      this.orderLookup.removeOrderEntryReference(refId);

      //logger.out.info(`DEV-TEST: LIFE-CYCLE Subscription REMOVED for: ${refId} | size = ${this.getter.determineLifeCycleActiveSubscriptions()}`);
    }
  }

  /**********************************************************************/
  /* Intervention subscription  */

  // INITIALIZE - SUBSCRIBE
  public initializeWorkerInterventionSubscriptionsForStrategy(refId: string): void {
    let strategyNodes: Array<string> = this.getter.getStrategyNodesListRoot(refId);
    strategyNodes.forEach((nodeRefId) => {
      this.initializeWorkerInterventionSubscriptionsForSingleNode(nodeRefId);
    });
  }

  public initializeWorkerInterventionSubscriptionsForSingleNode(refId: string): void {
    if (this.getter.has(refId) && !this.getter.isSubscriptionToInterventionEventsActive(refId)) {
      this.setter.setInterventionSubscriptionStatus(refId, true);
      this.interventionLookup.addOrderEntryReference(refId);
      //logger.out.info(`DEV-TEST: INTERVENTION Subscription STARTED for: ${refId} | size = ${this.getter.determineInterventionActiveSubscriptions()}`);
    }
  }

  // DESTROY - UNSUBSCRIBE
  public destroyWorkerInterventionSubscriptionsForStrategy(refId: string): void {
    let strategyNodes: Array<string> = this.getter.getStrategyNodesListRoot(refId);
    strategyNodes.forEach((nodeRefId: string) => {
      this.destroyWorkerInterventionSubscriptionsForSingleNode(nodeRefId);
    });
  }

  private destroyWorkerInterventionSubscriptionsForSingleNode(refId: string): void {
    if (this.getter.isSubscriptionToInterventionEventsActive(refId)) {
      if (this.sync.isTheRefIdForwarded(refId)) {
        this.sync.unregisterForward(refId);
      }
      this.setter.setInterventionSubscriptionStatus(refId, false);
      this.interventionLookup.removeOrderEntryReference(refId);
      //logger.out.info(`DEV-TEST: INTERVENTION Subscription REMOVED for: ${refId} | size = ${this.getter.determineInterventionActiveSubscriptions()}`);
    }
  }

  /**********************************************************************/
  /* Scratchpad subscription  */

  // INITIALIZE - SUBSCRIBE
  public initializeWorkerScratchpadSubscriptionsForStrategy(refId: string): void {
    let strategyNodes: Array<string> = this.getter.getStrategyNodesListRoot(refId);
    strategyNodes.forEach((nodeRefId) => {
      this.initializeWorkerScratchpadSubscriptionsForSingleNode(nodeRefId);
    });
  }

  private initializeWorkerScratchpadSubscriptionsForSingleNode(refId: string): void {
    if (this.getter.has(refId) && !this.getter.isSubscriptionToScratchpadEventsActive(refId)) {
      this.setter.setScratchpadSubscriptionStatus(refId, true);
      this.scratchpadLookup.addOrderEntryReference(refId);
      //logger.out.info(`DEV-TEST: SCRATCHPAD Subscription STARTED for: ${refId} | size = ${this.getter.determineScratchpadActiveSubscriptions()}`);
    }
  }

  // DESTROY - UNSUBSCRIBE
  public destroyWorkerScratchpadSubscriptionsForStrategy(refId: string): void {
    let strategyNodes: Array<string> = this.getter.getStrategyNodesListRoot(refId);
    strategyNodes.forEach((nodeRefId: string) => {
      this.destroyWorkerScratchpadSubscriptionsForSingleNode(nodeRefId);
    });
  }

  private destroyWorkerScratchpadSubscriptionsForSingleNode(refId: string): void {
    if (this.getter.isSubscriptionToScratchpadEventsActive(refId)) {
      if (this.sync.isTheRefIdForwarded(refId)) {
        this.sync.unregisterForward(refId);
      }
      this.setter.setScratchpadSubscriptionStatus(refId, false);
      this.interventionLookup.removeOrderEntryReference(refId);
      //logger.out.info(`DEV-TEST: INTERVENTION Subscription REMOVED for: ${refId} | size = ${this.getter.determineScratchpadActiveSubscriptions()}`);
    }
  }

  /**********************************************************************/
  /* HELPER - Error handler  */
  public determineHardCutoffError(errors: CubiqTS.Message[]): boolean {
    let result = false;
    if (Array.isArray(errors) && errors.length > 0) {
      errors.map((error: any) => {
        let errorMessages: any = JSON.parse(error.jsonMessage != null ? error.jsonMessage : error);

        if (!Array.isArray(errorMessages)) {
          errorMessages = [errorMessages];
        }

        errorMessages.forEach((jsonProperty) => {
          if (
            jsonProperty["id"] === BMARK_HCF_ERROR_ID ||
            jsonProperty["args"] === BMARK_HCF_ERROR_TEXT
          ) {
            result = true;
          }
        });
      });
    }
    return result;
  }

  public handleErrors(errors: CubiqTS.Message[]): string {
    let message = "";
    let errorCounter = 1;
    errors.map((error: any) => {
      let errorMessage: any = JSON.parse(error.jsonMessage != null ? error.jsonMessage : error);

      if (!Array.isArray(errorMessage)) {
        errorMessage = [errorMessage];
      }

      errorMessage.forEach(
        (jsonProperty) =>
          (message +=
            (errors.length > 1 ? errorCounter++ + ". " : "") +
            this.translate.instant(
              jsonProperty["id"],
              this.utility.sanityCheckArgs(jsonProperty["args"]),
            ) +
            (errorCounter > 1 && errorCounter <= errors.length ? "<br/>" : "")),
      );
    });

    return message;
  }

  public handleBulkRejectedErrors(errors: any): string {
    let message = "";
    let errorCounter = 1;
    // let err1:any = errors[0];
    // let err2:any = JSON.parse(errors[0]);
    errors
      .map((error) => JSON.parse(error.toString()))
      .forEach(
        (jsonProperty) =>
          (message +=
            (errors.length > 1 ? errorCounter++ + ". " : "") +
            this.translate.instant(
              jsonProperty["id"],
              this.utility.sanityCheckArgs(jsonProperty["args"]),
            ) +
            (errorCounter > 1 && errorCounter <= errors.length ? "<br/>" : "")),
      );
    return message;
  }

  /**********************************************************************/
  /* DEBUG - HELPER  */

  private outputLifeCycleProcessorQueueUpdate(
    refId: string,
    opr: string,
    messageId: string = null,
  ): void {
    let message: string =
      `OE-WORKER-LISTENER: [${typeof refId == "string" ? refId : "N/A"}] -> ${opr} ${
        messageId != null ? "| MsgId: " + messageId + " " : ""
      }` +
      `| Processor Status:  ${
        this.control.lifeCycle.processorBusy ? "BUSY" : "EMPTY"
      } | Queue size: ${this.control.lifeCycle.eventQueue.length}`;
    //logger.out.warn(message);// for dev testing - to be removed
    this.outputExtendedLogging(message);
  }

  private outputExtendedLogging(value: string, dump: any = null): void {
    //this.oeLogger.outputExtendedLogging(value, dump);
    if (dump == null) this.oeLogger.outputDevLogToConsole(value, false);
    else this.oeLogger.outputDevLogToConsole(value, false, dump);
  }
}
